import React from 'react';

export const PatternDemo = ({ colors }) => {
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    // Convert hex to RGB for transparency effects
    const hexToRgba = (hex, alpha = 1) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Pattern & Texture System</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Professional pattern library featuring 25+ geometric, organic, and modern designs for versatile applications.
                </p>
            </div>

            {/* Geometric Patterns */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Geometric Patterns</h5>

                <div className="space-y-8">
                    {/* Simple Stripes */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">STRIPE PATTERNS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Horizontal Stripes</h6>
                                    <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="horizontal-stripes" x="0" y="0" width="400" height="32" patternUnits="userSpaceOnUse">
                                                    <rect x="0" y="0" width="400" height="8" fill={getColor(0)} />
                                                    <rect x="0" y="8" width="400" height="8" fill={getColor(1)} />
                                                    <rect x="0" y="16" width="400" height="8" fill={getColor(2)} />
                                                    <rect x="0" y="24" width="400" height="8" fill={getColor(3)} />
                                                </pattern>
                                            </defs>
                                            <rect width="400" height="128" fill="url(#horizontal-stripes)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Equal width horizontal bands</p>
                                </div>

                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Vertical Stripes</h6>
                                    <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="vertical-stripes" x="0" y="0" width="32" height="128" patternUnits="userSpaceOnUse">
                                                    <rect x="0" y="0" width="8" height="128" fill={getColor(0)} />
                                                    <rect x="8" y="0" width="8" height="128" fill={getColor(1)} />
                                                    <rect x="16" y="0" width="8" height="128" fill={getColor(2)} />
                                                    <rect x="24" y="0" width="8" height="128" fill={getColor(3)} />
                                                </pattern>
                                            </defs>
                                            <rect width="400" height="128" fill="url(#vertical-stripes)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Vertical color bands</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Diagonal Stripes</h6>
                                    <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="diagonal-stripes" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                                    <rect x="0" y="0" width="14" height="28" fill={getColor(0)} />
                                                    <rect x="14" y="0" width="14" height="28" fill={getColor(1)} />
                                                </pattern>
                                            </defs>
                                            <rect width="400" height="128" fill="url(#diagonal-stripes)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">45-degree diagonal stripes</p>
                                </div>

                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Chevron Pattern</h6>
                                    <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="chevron" x="0" y="0" width="50" height="30" patternUnits="userSpaceOnUse">
                                                    <path d="M0,15 L25,0 L50,15" stroke={getColor(0)} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M0,30 L25,15 L50,30" stroke={getColor(1)} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
                                                    <path d="M0,15 L25,0 L50,15 L50,30 L25,15 L0,30 Z" fill={hexToRgba(getColor(2), 0.15)} />
                                                </pattern>
                                            </defs>
                                            <rect width="400" height="128" fill={hexToRgba(getColor(3), 0.05)} />
                                            <rect width="400" height="128" fill="url(#chevron)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Refined chevron design</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dot Patterns */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">DOT PATTERNS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Regular Grid</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="dot-grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                                <circle cx="10" cy="10" r="3" fill={getColor(0)} />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(4) || getColor(0), 0.05)} />
                                        <rect width="400" height="128" fill="url(#dot-grid)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Uniform dot spacing</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Polka Dots</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="polka-dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                                                <circle cx="20" cy="20" r="7" fill={getColor(0)} opacity="0.8" />
                                                <circle cx="20" cy="20" r="5" fill={getColor(1)} opacity="0.6" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(2), 0.1)} />
                                        <rect width="400" height="128" fill="url(#polka-dots)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Classic polka dot pattern</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Color Spectrum</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="spectrum-dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                                                <circle cx="8" cy="8" r="3" fill={getColor(0)} />
                                                <circle cx="24" cy="8" r="3" fill={getColor(1)} />
                                                <circle cx="8" cy="24" r="3" fill={getColor(2)} />
                                                <circle cx="24" cy="24" r="3" fill={getColor(3)} />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(0), 0.05)} />
                                        <rect width="400" height="128" fill="url(#spectrum-dots)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Multi-color dots</p>
                            </div>
                        </div>
                    </div>

                    {/* Grid & Line Patterns */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">GRID & LINE PATTERNS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Grid Lines</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="grid-lines" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke={getColor(0)} strokeWidth="0.5" opacity="0.5" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(1), 0.05)} />
                                        <rect width="400" height="128" fill="url(#grid-lines)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Classic graph paper grid</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Crosshatch</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="crosshatch" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
                                                <path d="M0,0 L16,16 M16,0 L0,16" stroke={getColor(0)} strokeWidth="1" opacity="0.4" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(2), 0.1)} />
                                        <rect width="400" height="128" fill="url(#crosshatch)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Diagonal crosshatch texture</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Diagonal Hatch</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="diagonal-hatch" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                                <line x1="0" y1="0" x2="0" y2="12" stroke={getColor(1)} strokeWidth="1.5" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(0), 0.05)} />
                                        <rect width="400" height="128" fill="url(#diagonal-hatch)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Angled hatching lines</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Complex Geometric Patterns */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Complex Geometric Patterns</h5>

                <div className="space-y-8">
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">TESSELLATIONS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Hexagonal Tessellation</h6>
                                    <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="hexagons" width="50" height="43.3" patternUnits="userSpaceOnUse">
                                                    <polygon
                                                        points="25,0 45,12.5 45,30.8 25,43.3 5,30.8 5,12.5"
                                                        fill={hexToRgba(getColor(0), 0.6)}
                                                        stroke={getColor(1)}
                                                        strokeWidth="1"
                                                    />
                                                </pattern>
                                            </defs>
                                            <rect width="500" height="160" fill={hexToRgba(getColor(2), 0.1)} />
                                            <rect width="500" height="160" fill="url(#hexagons)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Honeycomb structure</p>
                                </div>

                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Triangle Mosaic</h6>
                                    <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="triangles" x="0" y="0" width="60" height="52" patternUnits="userSpaceOnUse">
                                                    <polygon points="30,0 60,26 0,26" fill={getColor(0)} opacity="0.7" />
                                                    <polygon points="0,26 30,52 60,26" fill={getColor(1)} opacity="0.7" />
                                                    <polygon points="30,0 60,26 0,26" stroke={getColor(3)} strokeWidth="1" fill="none" />
                                                    <polygon points="0,26 30,52 60,26" stroke={getColor(2)} strokeWidth="1" fill="none" />
                                                </pattern>
                                            </defs>
                                            <rect width="500" height="160" fill="url(#triangles)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Triangular tessellation</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Islamic Geometric</h6>
                                    <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="islamic" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                                                    <rect x="0" y="0" width="100" height="100" fill={hexToRgba(getColor(3), 0.05)} />
                                                    {/* Outer star */}
                                                    <polygon
                                                        points="50,10 60,35 85,35 65,50 75,75 50,60 25,75 35,50 15,35 40,35"
                                                        fill={hexToRgba(getColor(0), 0.4)}
                                                        stroke={getColor(0)}
                                                        strokeWidth="1.5"
                                                    />
                                                    {/* Inner octagon */}
                                                    <polygon
                                                        points="50,25 62,32 62,48 50,55 38,48 38,32"
                                                        fill={hexToRgba(getColor(1), 0.5)}
                                                        stroke={getColor(1)}
                                                        strokeWidth="1"
                                                    />
                                                    {/* Center circle */}
                                                    <circle cx="50" cy="40" r="8" fill={getColor(2)} opacity="0.7" />
                                                    {/* Corner accents */}
                                                    <circle cx="10" cy="10" r="3" fill={getColor(0)} opacity="0.3" />
                                                    <circle cx="90" cy="10" r="3" fill={getColor(1)} opacity="0.3" />
                                                    <circle cx="10" cy="90" r="3" fill={getColor(2)} opacity="0.3" />
                                                    <circle cx="90" cy="90" r="3" fill={getColor(3)} opacity="0.3" />
                                                </pattern>
                                            </defs>
                                            <rect width="500" height="160" fill="url(#islamic)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Intricate star motif</p>
                                </div>

                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Celtic Knot</h6>
                                    <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="celtic" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                                                    <rect x="0" y="0" width="60" height="60" fill={hexToRgba(getColor(3), 0.05)} />
                                                    <path
                                                        d="M10,10 Q30,0 50,10 Q60,30 50,50 Q30,60 10,50 Q0,30 10,10"
                                                        fill="none"
                                                        stroke={getColor(0)}
                                                        strokeWidth="3"
                                                    />
                                                    <path
                                                        d="M50,10 Q70,30 50,50 Q30,60 10,50 Q0,30 10,10 Q30,0 50,10"
                                                        fill="none"
                                                        stroke={getColor(1)}
                                                        strokeWidth="2"
                                                        opacity="0.7"
                                                    />
                                                    <circle cx="30" cy="30" r="8" fill={getColor(2)} opacity="0.6" />
                                                </pattern>
                                            </defs>
                                            <rect width="500" height="160" fill="url(#celtic)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Interwoven Celtic design</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modern & Wave Patterns */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Modern & Wave Patterns</h5>

                <div className="space-y-8">
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">WAVE & CURVE PATTERNS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Smooth Waves</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="smooth-waves" x="0" y="0" width="100" height="40" patternUnits="userSpaceOnUse">
                                                <path d="M0,20 Q25,10 50,20 T100,20" fill="none" stroke={getColor(0)} strokeWidth="2" opacity="0.6" />
                                                <path d="M0,30 Q25,20 50,30 T100,30" fill="none" stroke={getColor(1)} strokeWidth="2" opacity="0.4" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(2), 0.05)} />
                                        <rect width="400" height="128" fill="url(#smooth-waves)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Flowing wave lines</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Layered Waves</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <path d="M0,40 Q100,20 200,40 T400,40 L400,128 L0,128 Z" fill={hexToRgba(getColor(0), 0.3)} />
                                        <path d="M0,60 Q100,40 200,60 T400,60 L400,128 L0,128 Z" fill={hexToRgba(getColor(1), 0.4)} />
                                        <path d="M0,80 Q100,60 200,80 T400,80 L400,128 L0,128 Z" fill={hexToRgba(getColor(2), 0.5)} />
                                        <path d="M0,100 Q100,80 200,100 T400,100 L400,128 L0,128 Z" fill={hexToRgba(getColor(3), 0.6)} />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Stacked wave layers</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Zigzag Pattern</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="zigzag" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                                                <path d="M0,20 L10,0 L20,20 L30,0 L40,20" fill="none" stroke={getColor(0)} strokeWidth="2" />
                                                <path d="M0,40 L10,20 L20,40 L30,20 L40,40" fill="none" stroke={getColor(1)} strokeWidth="2" opacity="0.6" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(2), 0.08)} />
                                        <rect width="400" height="128" fill="url(#zigzag)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sharp zigzag lines</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">MODERN GEOMETRIC</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Circle Grid</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="circle-grid" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                                                <circle cx="15" cy="15" r="12" fill="none" stroke={getColor(0)} strokeWidth="1" opacity="0.5" />
                                                <circle cx="15" cy="15" r="6" fill={getColor(1)} opacity="0.3" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(3), 0.05)} />
                                        <rect width="400" height="128" fill="url(#circle-grid)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Overlapping circles</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Diamond Mesh</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="diamond-mesh" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                                                <rect x="20" y="0" width="20" height="20" fill={getColor(0)} opacity="0.2" transform="rotate(45 20 20)" />
                                                <rect x="20" y="0" width="20" height="20" fill="none" stroke={getColor(1)} strokeWidth="1" transform="rotate(45 20 20)" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(2), 0.05)} />
                                        <rect width="400" height="128" fill="url(#diamond-mesh)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Rotated square grid</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Plus Pattern</h6>
                                <div className="h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 400 128" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="plus-pattern" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                                                <path d="M12,4 L12,20 M4,12 L20,12" stroke={getColor(0)} strokeWidth="2" strokeLinecap="round" />
                                            </pattern>
                                        </defs>
                                        <rect width="400" height="128" fill={hexToRgba(getColor(1), 0.05)} />
                                        <rect width="400" height="128" fill="url(#plus-pattern)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Plus sign repeats</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Organic & Decorative Patterns */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Organic & Decorative Patterns</h5>

                <div className="space-y-8">
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">NATURAL PATTERNS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Floral Motif</h6>
                                <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="floral" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                                                <rect x="0" y="0" width="80" height="80" fill={hexToRgba(getColor(3), 0.05)} />
                                                {/* Flower center */}
                                                <circle cx="40" cy="40" r="8" fill={getColor(0)} />
                                                {/* Petals */}
                                                <ellipse cx="40" cy="20" rx="6" ry="15" fill={getColor(1)} opacity="0.8" />
                                                <ellipse cx="60" cy="40" rx="15" ry="6" fill={getColor(1)} opacity="0.8" />
                                                <ellipse cx="40" cy="60" rx="6" ry="15" fill={getColor(1)} opacity="0.8" />
                                                <ellipse cx="20" cy="40" rx="15" ry="6" fill={getColor(1)} opacity="0.8" />
                                            </pattern>
                                        </defs>
                                        <rect width="500" height="160" fill="url(#floral)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Stylized flower pattern</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Organic Cells</h6>
                                <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                        <rect width="500" height="160" fill={hexToRgba(getColor(0), 0.05)} />
                                        {/* Generate organic cell-like shapes */}
                                        {Array.from({ length: 20 }).map((_, i) => {
                                            const x = (i * 37 + 29) % 280 + 10;
                                            const y = (i * 23 + 17) % 140 + 10;
                                            const rx = 8 + (i % 5) * 3;
                                            const ry = 6 + (i % 4) * 2;
                                            const rotation = (i * 17) % 360;
                                            const colorIndex = i % colors.length;
                                            return (
                                                <ellipse
                                                    key={i}
                                                    cx={x}
                                                    cy={y}
                                                    rx={rx}
                                                    ry={ry}
                                                    fill={colors[colorIndex]?.value}
                                                    opacity={0.4 + (i % 3) * 0.15}
                                                    transform={`rotate(${rotation} ${x} ${y})`}
                                                />
                                            );
                                        })}
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cell-like organic shapes</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Leaf Pattern</h6>
                                <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="leaves" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                                                <ellipse cx="30" cy="30" rx="20" ry="12" fill={getColor(0)} opacity="0.5" transform="rotate(45 30 30)" />
                                                <ellipse cx="30" cy="30" rx="18" ry="10" fill={getColor(1)} opacity="0.6" transform="rotate(45 30 30)" />
                                                <path d="M30,18 Q30,30 30,42" stroke={getColor(2)} strokeWidth="1.5" opacity="0.7" />
                                            </pattern>
                                        </defs>
                                        <rect width="500" height="160" fill={hexToRgba(getColor(3), 0.05)} />
                                        <rect width="500" height="160" fill="url(#leaves)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Natural leaf motifs</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Scattered Shapes</h6>
                                <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                        <rect width="500" height="160" fill={hexToRgba(getColor(2), 0.05)} />
                                        <defs>
                                            <pattern id="scattered" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                                                <circle cx="20" cy="20" r="4" fill={getColor(0)} opacity="0.6" />
                                                <rect x="50" y="10" width="8" height="8" fill={getColor(1)} opacity="0.5" transform="rotate(30 54 14)" />
                                                <polygon points="30,50 35,60 25,60" fill={getColor(2)} opacity="0.6" />
                                                <circle cx="60" cy="55" r="3" fill={getColor(3)} opacity="0.5" />
                                            </pattern>
                                        </defs>
                                        <rect width="500" height="160" fill="url(#scattered)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Random shape distribution</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Bubble Pattern</h6>
                                <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            <pattern id="bubbles" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                                                <circle cx="15" cy="15" r="10" fill={hexToRgba(getColor(0), 0.3)} stroke={getColor(0)} strokeWidth="1" />
                                                <circle cx="45" cy="15" r="6" fill={hexToRgba(getColor(1), 0.3)} stroke={getColor(1)} strokeWidth="1" />
                                                <circle cx="30" cy="45" r="8" fill={hexToRgba(getColor(2), 0.3)} stroke={getColor(2)} strokeWidth="1" />
                                                <circle cx="50" cy="45" r="4" fill={hexToRgba(getColor(3), 0.3)} stroke={getColor(3)} strokeWidth="1" />
                                            </pattern>
                                        </defs>
                                        <rect width="500" height="160" fill={hexToRgba(getColor(0), 0.05)} />
                                        <rect width="500" height="160" fill="url(#bubbles)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Floating bubble effect</p>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Abstract Blobs</h6>
                                <div className="h-40 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <svg width="100%" height="100%" viewBox="0 0 500 160" preserveAspectRatio="xMidYMid slice">
                                        <rect width="500" height="160" fill={hexToRgba(getColor(3), 0.05)} />
                                        <defs>
                                            <pattern id="blobs" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                                                <path
                                                    d="M50,20 Q70,30 60,50 T40,70 Q20,60 30,40 T50,20"
                                                    fill={hexToRgba(getColor(0), 0.3)}
                                                    stroke={getColor(1)}
                                                    strokeWidth="1"
                                                />
                                                <path
                                                    d="M80,60 Q90,70 85,85 T65,90 Q55,85 60,70 T80,60"
                                                    fill={hexToRgba(getColor(2), 0.3)}
                                                    stroke={getColor(3)}
                                                    strokeWidth="1"
                                                />
                                            </pattern>
                                        </defs>
                                        <rect width="500" height="160" fill="url(#blobs)" />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Organic blob shapes</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pattern Applications */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Pattern Applications</h5>

                <div className="space-y-8">
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">DESIGN CONTEXTS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div>
                                <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Background Textures</h6>
                                <div className="space-y-3">
                                    <div className="p-6 rounded-lg relative overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(0), 0.05) }}>
                                        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="bg-texture" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                                    <circle cx="10" cy="10" r="1" fill={getColor(0)} opacity="0.1" />
                                                </pattern>
                                            </defs>
                                            <rect width="100%" height="100%" fill="url(#bg-texture)" />
                                        </svg>
                                        <div className="relative z-10">
                                            <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">Subtle Background</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Perfect for cards, sections, and content areas that need texture without distraction.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Decorative Elements</h6>
                                <div className="space-y-3">
                                    <div className="flex items-center p-4 rounded-lg bg-gray-50 dark:bg-gray-900">
                                        <div className="w-12 h-12 rounded-lg mr-4 flex items-center justify-center" style={{ backgroundColor: hexToRgba(getColor(0), 0.1) }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24">
                                                <defs>
                                                    <pattern id="icon-pattern" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                                                        <circle cx="4" cy="4" r="1" fill={getColor(0)} />
                                                    </pattern>
                                                </defs>
                                                <rect width="24" height="24" fill="url(#icon-pattern)" rx="4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-gray-900 dark:text-white">Patterned Icons</h5>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Icons with subtle pattern fills</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pattern Specifications */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Pattern Specifications</h5>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Technical Guidelines</h6>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Pattern Repeat</span>
                                <span className="text-sm font-mono">20px - 80px optimal</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Background Opacity</span>
                                <span className="text-sm font-mono">5% - 15% for readability</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Accent Opacity</span>
                                <span className="text-sm font-mono">40% - 80% for emphasis</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">SVG Pattern Units</span>
                                <span className="text-sm font-mono">userSpaceOnUse</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Color Applications</h6>
                        <div className="space-y-3">
                            {colors.slice(0, 4).map((color, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                                        <svg width="20" height="20" viewBox="0 0 20 20">
                                            <defs>
                                                <pattern id={`spec-pattern-${index}`} x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
                                                    <circle cx="2" cy="2" r="0.5" fill={color.value} />
                                                </pattern>
                                            </defs>
                                            <rect width="20" height="20" fill={`url(#spec-pattern-${index})`} rx="2" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                                        {['Primary patterns & main backgrounds', 'Secondary accents & borders', 'Highlight patterns & CTAs', 'Supporting textures & fills'][index]}
                                    </span>
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                        {color.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};